// macadamia-app/src/components/pages/jurisprudences/FiltersModal.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../../utils/apiClient';

// Types pour les données de filtres
interface FilterOption {
    label: string;
    value: string;
    count?: number;
}

interface FiltersData {
    legalDomains: FilterOption[];
    subDomains: FilterOption[];
    jurisdictions: FilterOption[];
}

interface ActiveFilter {
    type: 'mainDomain' | 'subDomain' | 'jurisdiction';
    value: string;
    label: string;
}

interface FiltersModalProps {
    isOpen: boolean;
    onClose: () => void;
    activeFilters: ActiveFilter[];
    onFiltersChange: (filters: ActiveFilter[]) => void;
}

const FiltersModal: React.FC<FiltersModalProps> = ({
    isOpen,
    onClose,
    activeFilters,
    onFiltersChange
}) => {
    const [filtersData, setFiltersData] = useState<FiltersData>({
        legalDomains: [],
        subDomains: [],
        jurisdictions: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerms, setSearchTerms] = useState({
        legalDomains: '',
        subDomains: '',
        jurisdictions: ''
    });

    // État spécifique pour la recherche de sous-domaines
    const [subDomainSearchResults, setSubDomainSearchResults] = useState<FilterOption[]>([]);
    const [isSearchingSubDomains, setIsSearchingSubDomains] = useState(false);
    const [hasSearchedSubDomains, setHasSearchedSubDomains] = useState(false);

    // État local pour les sélections avant application
    const [localSelections, setLocalSelections] = useState<{
        legalDomains: string[];
        subDomains: string[];
        jurisdictions: string[];
    }>({
        legalDomains: [],
        subDomains: [],
        jurisdictions: []
    });

    // Charger les données de filtres depuis l'API
    useEffect(() => {
        if (isOpen) {
            fetchFiltersData();
        }
    }, [isOpen]);

    // Synchroniser les sélections locales avec les filtres actifs
    useEffect(() => {
        if (isOpen) {
            setLocalSelections({
                legalDomains: activeFilters.filter(f => f.type === 'mainDomain').map(f => f.value),
                subDomains: activeFilters.filter(f => f.type === 'subDomain').map(f => f.value),
                jurisdictions: activeFilters.filter(f => f.type === 'jurisdiction').map(f => f.value)
            });
        }
    }, [isOpen, activeFilters]);

    // Recherche de sous-domaines avec debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerms.subDomains.length >= 2) {
                searchSubDomains(searchTerms.subDomains);
            } else {
                setSubDomainSearchResults([]);
                setHasSearchedSubDomains(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerms.subDomains]);

    const fetchFiltersData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiClient('/jurisprudence-metadata/filters');

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success && data.data) {
                setFiltersData(data.data);
            } else {
                throw new Error('Réponse API invalide');
            }
        } catch (error) {
            console.error('Erreur lors du chargement des filtres:', error);
            setError(error instanceof Error ? error.message : 'Erreur inconnue');
        } finally {
            setLoading(false);
        }
    };

    // Recherche textuelle de sous-domaines
    const searchSubDomains = async (searchTerm: string) => {
        setIsSearchingSubDomains(true);
        try {
            const response = await apiClient(`/jurisprudence-metadata/filters/sub-domains/search?query=${encodeURIComponent(searchTerm)}`);

            const data = await response.json();
            if (data.success) {
                setSubDomainSearchResults(data.data.subDomains || []);
                setHasSearchedSubDomains(true);
            }
        } catch (error) {
            console.error('Erreur recherche sous-domaines:', error);
        } finally {
            setIsSearchingSubDomains(false);
        }
    };

    // Gestion des sélections locales
    const handleSelectionChange = (type: keyof typeof localSelections, value: string, checked: boolean) => {
        setLocalSelections(prev => ({
            ...prev,
            [type]: checked
                ? [...prev[type], value]
                : prev[type].filter(item => item !== value)
        }));
    };

    // Application des filtres
    const applyFilters = () => {
        const newFilters: ActiveFilter[] = [];

        // Domaines principaux
        localSelections.legalDomains.forEach(value => {
            const option = filtersData.legalDomains.find(d => d.value === value);
            if (option) {
                newFilters.push({
                    type: 'mainDomain',
                    value: option.value,
                    label: option.label
                });
            }
        });

        // Sous-domaines - combiner les sources
        localSelections.subDomains.forEach(value => {
            // Chercher d'abord dans les données principales
            let option = filtersData.subDomains.find(d => d.value === value);
            // Sinon chercher dans les résultats de recherche
            if (!option) {
                option = subDomainSearchResults.find(d => d.value === value);
            }
            if (option) {
                newFilters.push({
                    type: 'subDomain',
                    value: option.value,
                    label: option.label
                });
            }
        });

        // Juridictions
        localSelections.jurisdictions.forEach(value => {
            const option = filtersData.jurisdictions.find(j => j.value === value);
            if (option) {
                newFilters.push({
                    type: 'jurisdiction',
                    value: option.value,
                    label: option.label
                });
            }
        });

        onFiltersChange(newFilters);
        onClose();
    };

    // Réinitialisation des filtres
    const resetFilters = () => {
        setLocalSelections({
            legalDomains: [],
            subDomains: [],
            jurisdictions: []
        });
        setSubDomainSearchResults([]);
        setHasSearchedSubDomains(false);
    };

    // Filtrage avec recherche pour domaines et juridictions
    const getFilteredOptions = (options: FilterOption[], searchTerm: string) => {
        if (!searchTerm.trim()) return options;
        return options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    // Obtenir les sous-domaines à afficher (TOP 100 ou résultats de recherche)
    const getDisplayedSubDomains = () => {
        if (hasSearchedSubDomains && searchTerms.subDomains.length >= 2) {
            return subDomainSearchResults; // Résultats de recherche
        }
        return filtersData.subDomains; // TOP 100 par défaut
    };

    const getTotalActiveFilters = () => {
        return Object.values(localSelections).reduce((total, selections) => total + selections.length, 0);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay sombre */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 bg-black bg-opacity-50 z-40 margin0"
                        onClick={onClose}
                    />

                    {/* Modal qui glisse depuis la gauche */}
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 left-0 h-full w-96 bg-white dark:bg-mcdm-third-300 shadow-xl z-50 overflow-y-auto margin0"
                    >
                        {/* En-tête du modal */}
                        <div className="sticky top-0 bg-white dark:bg-mcdm-third-300 border-b border-gray-200 dark:border-gray-600 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-lg font-platypi font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
                                Filtres
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-mcdm-third-400 rounded-lg transition-colors duration-200"
                            >
                                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenu du modal */}
                        <div className="px-6 py-6 space-y-6">
                            {/* État de chargement */}
                            {loading && (
                                <div className="text-center py-8">
                                    <div className="inline-flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-mcdm-blue-100 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-mcdm-third-500 dark:text-mcdm-primary-100">Chargement des filtres...</span>
                                    </div>
                                </div>
                            )}

                            {/* Erreur */}
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
                                    <div className="flex">
                                        <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                                Erreur de chargement
                                            </h3>
                                            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                                                {error}
                                            </p>
                                            <button
                                                onClick={fetchFiltersData}
                                                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500 underline"
                                            >
                                                Réessayer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Contenu des filtres */}
                            {!loading && !error && (
                                <>
                                    {/* Section Domaines Principaux */}
                                    <div>
                                        <h3 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                                            Domaines Principaux ({filtersData.legalDomains.length})
                                        </h3>

                                        {/* Barre de recherche */}
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                placeholder="Rechercher un domaine..."
                                                value={searchTerms.legalDomains}
                                                onChange={(e) => setSearchTerms(prev => ({ ...prev, legalDomains: e.target.value }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-mcdm-third-400 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-mcdm-blue-100 focus:border-transparent"
                                            />
                                        </div>

                                        <div className="max-h-40 overflow-y-auto space-y-2">
                                            {getFilteredOptions(filtersData.legalDomains, searchTerms.legalDomains).map((domain) => (
                                                <label key={domain.value} className="flex items-center space-x-3 cursor-pointer group">
                                                    <input
                                                        type="checkbox"
                                                        checked={localSelections.legalDomains.includes(domain.value)}
                                                        onChange={(e) => handleSelectionChange('legalDomains', domain.value, e.target.checked)}
                                                        className="w-4 h-4 text-mcdm-blue-100 bg-gray-100 border-gray-300 rounded focus:ring-mcdm-blue-100 dark:focus:ring-mcdm-blue-100 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-mcdm-blue-100 transition-colors duration-200 flex-1">
                                                        {domain.label}
                                                    </span>
                                                    {domain.count && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                                                            {domain.count}
                                                        </span>
                                                    )}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Section Sous-domaines AVEC RECHERCHE INTELLIGENTE */}
                                    <div>
                                        <h3 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                                            Sous-domaines
                                            {hasSearchedSubDomains ? (
                                                <span className="text-xs text-gray-500 ml-2">
                                                    ({subDomainSearchResults.length} résultats)
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-500 ml-2">
                                                    (top {filtersData.subDomains.length})
                                                </span>
                                            )}
                                        </h3>

                                        {/* Barre de recherche intelligente */}
                                        <div className="mb-3">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher parmi tous les sous-domaines..."
                                                    value={searchTerms.subDomains}
                                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, subDomains: e.target.value }))}
                                                    className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-mcdm-third-400 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-mcdm-green-100 focus:border-transparent"
                                                />
                                                {isSearchingSubDomains && (
                                                    <div className="absolute right-3 top-2.5">
                                                        <div className="w-4 h-4 border-2 border-mcdm-green-100 border-t-transparent rounded-full animate-spin"></div>
                                                    </div>
                                                )}
                                            </div>
                                            {searchTerms.subDomains.length >= 2 && (
                                                <p className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200 mt-1">
                                                    {hasSearchedSubDomains ?
                                                        `${subDomainSearchResults.length} résultats trouvés` :
                                                        'Recherche en cours...'
                                                    }
                                                </p>
                                            )}
                                            {searchTerms.subDomains.length < 2 && searchTerms.subDomains.length > 0 && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    Tapez au moins 2 caractères pour rechercher
                                                </p>
                                            )}
                                        </div>

                                        <div className="max-h-40 overflow-y-auto space-y-2">
                                            {getDisplayedSubDomains().map((domain) => (
                                                <label key={domain.value} className="flex items-center space-x-3 cursor-pointer group">
                                                    <input
                                                        type="checkbox"
                                                        checked={localSelections.subDomains.includes(domain.value)}
                                                        onChange={(e) => handleSelectionChange('subDomains', domain.value, e.target.checked)}
                                                        className="w-4 h-4 text-mcdm-green-100 bg-gray-100 border-gray-300 rounded focus:ring-mcdm-green-100 dark:focus:ring-mcdm-green-100 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-mcdm-green-100 transition-colors duration-200 flex-1">
                                                        {domain.label}
                                                    </span>
                                                    {domain.count && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                                                            {domain.count}
                                                        </span>
                                                    )}
                                                </label>
                                            ))}
                                        </div>

                                    </div>

                                    {/* Section Juridictions */}
                                    <div>
                                        <h3 className="text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-3">
                                            Juridictions ({filtersData.jurisdictions.length})
                                        </h3>

                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                placeholder="Rechercher une juridiction..."
                                                value={searchTerms.jurisdictions}
                                                onChange={(e) => setSearchTerms(prev => ({ ...prev, jurisdictions: e.target.value }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-mcdm-third-400 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            />
                                        </div>

                                        <div className="max-h-40 overflow-y-auto space-y-2">
                                            {getFilteredOptions(filtersData.jurisdictions, searchTerms.jurisdictions).map((jurisdiction) => (
                                                <label key={jurisdiction.value} className="flex items-center space-x-3 cursor-pointer group">
                                                    <input
                                                        type="checkbox"
                                                        checked={localSelections.jurisdictions.includes(jurisdiction.value)}
                                                        onChange={(e) => handleSelectionChange('jurisdictions', jurisdiction.value, e.target.checked)}
                                                        className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-purple-600 transition-colors duration-200">
                                                        {jurisdiction.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Actions du modal */}
                        <div className="sticky bottom-0 bg-white dark:bg-mcdm-third-300 border-t border-gray-200 dark:border-gray-600 px-6 py-4 space-y-3">
                            <div className="flex gap-3">
                                <button
                                    onClick={applyFilters}
                                    disabled={loading}
                                    className="flex-1 px-4 py-2 bg-mcdm-green-400 text-white rounded-lg hover:bg-mcdm-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                                >
                                    Appliquer les filtres
                                </button>
                                <button
                                    onClick={resetFilters}
                                    disabled={loading}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-mcdm-third-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                    Réinitialiser
                                </button>
                            </div>

                            {/* Indicateur du nombre de filtres actifs */}
                            <div className="text-center">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {getTotalActiveFilters()} filtre{getTotalActiveFilters() > 1 ? 's' : ''} sélectionné{getTotalActiveFilters() > 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default FiltersModal;